import { useEffect } from "react";
import Footer from "@/components/landing/layout/Footer";
import Header from "@/components/landing/layout/Header";
import { ChevronLeft } from "lucide-react";
import { Link } from "react-router-dom";
import Lenis from "lenis";
import { useGSAP } from "@/hooks/useGSAP";

const claimExamples = [
  {
    scenario: "Bikini stained permanently by spilled drink at a resort.",
    covered: "Yes",
    reason: "Single accidental spill covered.",
  },
  {
    scenario: "Beach cover-up torn by pet jumping on it.",
    covered: "Yes",
    reason: "Accidental pet damage covered.",
  },
  {
    scenario: "Swimwear snagged on sharp bench corner during travel.",
    covered: "Yes",
    reason: "Sudden accidental tear covered.",
  },
  {
    scenario: "Colour fading from repeated sun/chlorine exposure.",
    covered: "No",
    reason: "Gradual wear & tear excluded.",
  },
  {
    scenario: "Damaged strap from intentional cutting.",
    covered: "No",
    reason: "Intentional misuse not covered.",
  },
  {
    scenario: "Defective stitching noticed on arrival.",
    covered: "No",
    reason: "Covered under manufacturer warranty, not this plan.",
  },
];

const ProtegaProtectionPlan: React.FC = () => {
  useEffect(() => {
    if ("scrollRestoration" in window.history) {
      window.history.scrollRestoration = "manual";
    }

    const lenis = new Lenis({
      duration: 1.6,
      easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
      orientation: "vertical",
      smoothWheel: true,
      touchMultiplier: 2,
    });

    function raf(time: number) {
      lenis.raf(time);
      requestAnimationFrame(raf);
    }

    requestAnimationFrame(raf);

    return () => {
      lenis.destroy();
    };
  }, []);

  useGSAP();

  return (
    <div className="tw-min-h-screen tw-bg-white tw-text-black">
      <Header />
      <main className="tw-relative">
        <div className="tw-h-20 lg:tw-h-24" />

        <section className="tw-max-w-5xl tw-mx-auto tw-px-6 tw-py-12 tw-font-mori tw-text-[#1C1E21]">
          {/* Header / Title */}
          <h1 className="tw-text-3xl md:tw-text-4xl tw-font-bold tw-mb-1">
            Protega Accidental Damage Protection Plan
          </h1>
          <p className="tw-text-sm tw-mb-6">
            For Products Purchased on The Beach Company
          </p>

          {/* 1. Introduction */}
          <section className="tw-mb-8">
            <h2 className="tw-font-semibold tw-text-lg tw-mb-2">
              1. Introduction
            </h2>
            <p className="tw-mb-4">
              Welcome, and thank you for choosing the Protega Accidental Damage
              Protection Plan. This document describes your coverage benefits,
              claim procedures, eligibility, exclusions, your rights as a
              consumer, and legal & privacy terms.
            </p>
            <p className="tw-mb-4">
              This Protection Plan is an optional, paid service contract. It is
              not an insurance policy, nor an extended warranty or repair
              contract. It offers additional protection and is not a substitute
              for any manufacturer warranty or statutory consumer guarantee.
            </p>

            <p className="tw-text-sm">
              Governing Law: Laws of India
              <br />
              Jurisdiction: Mumbai High Court (exclusive)
              <br />
              Administered by: EmbedMike India Pvt. Ltd. (“Protega”)
              <br />
              Merchant: The Beach Company
            </p>
          </section>

          {/* 2. Definitions */}
          <section className="tw-mb-8">
            <h2 className="tw-font-semibold tw-text-lg tw-mb-2">
              2. Definitions
            </h2>

            <div className="tw-overflow-x-auto tw-rounded tw-border tw-border-gray-200">
              <table className="tw-w-full tw-table-fixed tw-text-sm">
                <thead className="tw-bg-gray-100">
                  <tr>
                    <th className="tw-p-3 tw-border-r tw-text-left tw-w-1/3">
                      Term
                    </th>
                    <th className="tw-p-3 tw-text-left">Meaning</th>
                  </tr>
                </thead>
                <tbody>
                  <tr className="odd:tw-bg-white even:tw-bg-gray-50">
                    <td className="tw-p-3 tw-border-r">Customer</td>
                    <td className="tw-p-3">
                      End-user who purchases a product from The Beach Company
                      and opts for Protega Protection.
                    </td>
                  </tr>

                  <tr className="odd:tw-bg-white even:tw-bg-gray-50">
                    <td className="tw-p-3 tw-border-r">Merchant</td>
                    <td className="tw-p-3">The Beach Company</td>
                  </tr>

                  <tr className="odd:tw-bg-white even:tw-bg-gray-50">
                    <td className="tw-p-3 tw-border-r">Protega</td>
                    <td className="tw-p-3">
                      EmbedMike India Pvt. Ltd., providing the Protection Plan
                      and claims services.
                    </td>
                  </tr>

                  <tr className="odd:tw-bg-white even:tw-bg-gray-50">
                    <td className="tw-p-3 tw-border-r">Product</td>
                    <td className="tw-p-3">
                      Eligible swimwear, beachwear, or accessories purchased
                      from The Beach Company.
                    </td>
                  </tr>

                  <tr className="odd:tw-bg-white even:tw-bg-gray-50">
                    <td className="tw-p-3 tw-border-r">
                      Accidental Damage (AD)
                    </td>
                    <td className="tw-p-3">
                      Physical damage from an unexpected, external,
                      unintentional event (e.g., snags, liquid spills, pet
                      damage).
                    </td>
                  </tr>

                  <tr className="odd:tw-bg-white even:tw-bg-gray-50">
                    <td className="tw-p-3 tw-border-r">Protection Plan</td>
                    <td className="tw-p-3">
                      The plan purchased: AD (12 / 24 months) as shown on the
                      covered invoice.
                    </td>
                  </tr>

                  <tr className="odd:tw-bg-white even:tw-bg-gray-50">
                    <td className="tw-p-3 tw-border-r">Sum Insured</td>
                    <td className="tw-p-3">
                      Invoice value of the product (exclusive of taxes and
                      fees).
                    </td>
                  </tr>

                  <tr className="odd:tw-bg-white even:tw-bg-gray-50">
                    <td className="tw-p-3 tw-border-r">Policy Period</td>
                    <td className="tw-p-3">12 or 24 months from delivery date.</td>
                  </tr>

                  <tr className="odd:tw-bg-white even:tw-bg-gray-50">
                    <td className="tw-p-3 tw-border-r">Claim</td>
                    <td className="tw-p-3">
                      Customer’s request for repair, replacement, or
                      reimbursement under this Plan.
                    </td>
                  </tr>

                  <tr className="odd:tw-bg-white even:tw-bg-gray-50">
                    <td className="tw-p-3 tw-border-r">Claim Limit</td>
                    <td className="tw-p-3">
                      One valid claim per eligible Product per Policy Period;
                      payout up to purchase price.
                    </td>
                  </tr>
                </tbody>
              </table>
            </div>
          </section>

          {/* 3 - 5 */}
          <section className="tw-mb-8 tw-space-y-6">
            <div>
              <h2 className="tw-font-semibold tw-text-lg tw-mb-2">
                3. Plan Scope & Key Features
              </h2>
              <ul className="tw-list-disc tw-list-inside tw-text-sm tw-space-y-1">
                <li>
                  Accidental Damage (AD): Physical, visible damage from an
                  unexpected event post-delivery.
                </li>
                <li>
                  Includes single tears, liquid spills (excluding misuse), and
                  pet-related accidents.
                </li>
                <li>
                  Applies only after successful delivery and for normal consumer
                  use.
                </li>
              </ul>
            </div>

            <div>
              <h2 className="tw-font-semibold tw-text-lg tw-mb-2">
                4. What is Covered
              </h2>
              <ul className="tw-list-disc tw-list-inside tw-text-sm tw-space-y-1">
                <li>
                  Single-incident visible accidental damage including tears,
                  rips, or snags from accidental pulls or falls.
                </li>
                <li>
                  Accidental liquid spills and stains (e.g., sunscreen,
                  beverages) not from misuse or repeated exposure.
                </li>
                <li>Pet-related accidental damage (tearing, scratching, biting).</li>
                <li>
                  Scorch or burn marks due to accidental contact with hot
                  surfaces.
                </li>
                <li>
                  All incidents must occur after delivery and during normal
                  personal use.
                </li>
              </ul>
            </div>

            <div>
              <h2 className="tw-font-semibold tw-text-lg tw-mb-2">
                5. What is Not Covered (Exclusions)
              </h2>
              <ul className="tw-list-disc tw-list-inside tw-text-sm tw-space-y-1">
                <li>
                  Products not purchased from The Beach Company or ineligible
                  categories.
                </li>
                <li>Damage existing or noticed before purchase of the Plan.</li>
                <li>Intentional acts, gross negligence, or abusive use.</li>
                <li>
                  Use outside normal consumer/personal use (e.g., rental,
                  commercial).
                </li>
                <li>
                  Manufacturing defects or failures covered by original
                  warranty.
                </li>
                <li>
                  Cosmetic issues not affecting usability (minor scuffs, fading,
                  chlorine/sun-related colour change).
                </li>
                <li>
                  Normal wear & tear, corrosion, or gradual deterioration from
                  repeated washing.
                </li>
                <li>Unauthorized or self-repair.</li>
                <li>Damage after resale.</li>
                <li>Natural disasters, war, riot, pest infestation.</li>
                <li>Loss of items after successful delivery.</li>
                <li>
                  Failure to provide required documents or claims filed after
                  policy expiry.
                </li>
                <li>Repeated small damages accumulating over time.</li>
              </ul>
            </div>
          </section>

          {/* 6. Filing a Claim */}
          <section className="tw-mb-8">
            <h2 className="tw-font-semibold tw-text-lg tw-mb-3">
              6. Filing a Claim
            </h2>
            <ol className="tw-list-decimal tw-list-inside tw-text-sm tw-space-y-2">
              <li>
                Contact Protega via{" "}
                <a className="tw-underline" href="mailto:support@protega.ai">
                  support@protega.ai
                </a>{" "}
                or visit{" "}
                <span className="tw-font-medium">
                  www.protega.ai/customer-login
                </span>
                .
              </li>
              <li>Provide policy certificate and purchase invoice (required).</li>
              <li>Submit product photos (full + close-up of damage).</li>
              <li>
                Share details: date, time, location and description of the
                incident.
              </li>
              <li>
                Do not ship product until instructed by Protega. Await claim
                approval for repair, replacement, or payout.
              </li>
              <li>
                Protega may arrange repair, replacement, or payout (up to sum
                insured). Unauthorized repairs void coverage.
              </li>
              <li>
                Only one claim per product per plan. After settlement, the plan
                for that product expires.
              </li>
            </ol>
          </section>

          {/* 7. Cancellations & Refunds */}
          <section className="tw-mb-8">
            <h2 className="tw-font-semibold tw-text-lg tw-mb-3">
              7. Cancellations & Refunds
            </h2>
            <ul className="tw-list-disc tw-list-inside tw-text-sm tw-space-y-2">
              <li>Customer may cancel anytime.</li>
              <li>If cancelled within 30 days and no claim filed: full refund.</li>
              <li>Provider may cancel for fraud, misrepresentation, or non-payment.</li>
              <li>Refunds processed within 30 days of approval.</li>
            </ul>
          </section>

          {/* 8. Legal Terms & General Conditions */}
          <section className="tw-mb-8">
            <h2 className="tw-font-semibold tw-text-lg tw-mb-3">
              8. Legal Terms & General Conditions
            </h2>
            <ul className="tw-text-sm tw-space-y-2 tw-list-inside tw-list-disc">
              <li>
                <strong>Jurisdiction:</strong> Laws of India; exclusive courts
                of Mumbai.
              </li>
              <li>
                <strong>Dispute Resolution:</strong> Arbitration per Arbitration
                & Conciliation Act, 1996.
              </li>
              <li>
                <strong>Entire Agreement:</strong> This document and purchase
                confirmation form the agreement.
              </li>
              <li>
                <strong>Privacy:</strong> Data handled per Protega’s Privacy
                Policy.
              </li>
              <li>
                <strong>Limitation of Liability:</strong> Protega’s liability
                capped at sum insured; no indirect or consequential damages.
              </li>
            </ul>
          </section>

          {/* 9. FAQ (short) */}
          <section className="tw-mb-8">
            <h2 className="tw-font-semibold tw-text-lg tw-mb-3">
              9. Filing a Claim (FAQ)
            </h2>
            <div className="tw-text-sm tw-space-y-3">
              <div>
                <p className="tw-font-semibold">
                  Q: Is this warranty or insurance?
                </p>
                <p>A: No. It is a separate, optional protection plan.</p>
              </div>

              <div>
                <p className="tw-font-semibold">Q: When does coverage start?</p>
                <p>A: On delivery date; valid for 12 or 24 months.</p>
              </div>

              <div>
                <p className="tw-font-semibold">Q: How many claims allowed?</p>
                <p>A: One per product per policy period.</p>
              </div>

              <div>
                <p className="tw-font-semibold">Q: Is plan transferable?</p>
                <p>A: No.</p>
              </div>

              <div>
                <p className="tw-font-semibold">
                  Q: How do I track claim status?
                </p>
                <p>A: Updates shared via email/SMS after filing.</p>
              </div>
            </div>
          </section>

          {/* 10. Claim Examples */}
          <section className="tw-mb-8">
            <h2 className="tw-font-semibold tw-text-lg tw-mb-3">
              10. Filing a Claim (Examples)
            </h2>

            <div className="tw-overflow-x-auto tw-rounded tw-border tw-border-gray-200">
              <table className="tw-w-full tw-text-sm tw-table-fixed">
                <thead className="tw-bg-gray-100">
                  <tr>
                    <th className="tw-p-3 tw-border-r tw-text-left md:tw-w-1/2">
                      Scenario
                    </th>
                    <th className="tw-p-3 tw-border-r tw-text-left">Covered?</th>
                    <th className="tw-p-3 tw-text-left">Reason</th>
                  </tr>
                </thead>
                <tbody>
                  {claimExamples.map((ex, i) => (
                    <tr
                      key={i}
                      className={i % 2 === 0 ? "tw-bg-white" : "tw-bg-gray-50"}
                    >
                      <td className="tw-p-3 tw-border-r">{ex.scenario}</td>
                      <td className="tw-p-3 tw-border-r">{ex.covered}</td>
                      <td className="tw-p-3">{ex.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>

          {/* 11. Service Commitment */}
          <section className="tw-mb-8">
            <h2 className="tw-font-semibold tw-text-lg tw-mb-3">
              11. Service Commitment
            </h2>
            <ul className="tw-list-disc tw-list-inside tw-text-sm tw-space-y-2">
              <li>Policy document sent by email after plan purchase.</li>
              <li>Claims responded to within 24 hours.</li>
              <li>Pickup/inspection arranged promptly if required.</li>
              <li>Dedicated Protega & The Beach Company support.</li>
            </ul>
          </section>

          {/* 12. Contact Us */}
          <section className="tw-mb-12">
            <h2 className="tw-font-semibold tw-text-lg tw-mb-3">
              12. Contact Us
            </h2>
            <div className="tw-text-sm tw-space-y-2">
              <p>
                <strong>Protega Claims & Support:</strong>
              </p>
              <p>
                Email:{" "}
                <a className="tw-underline" href="mailto:support@protega.ai">
                  support@protega.ai
                </a>
              </p>
              <p>
                Web:{" "}
                <a
                  className="tw-underline"
                  href="https://www.protega.ai/customer-login"
                >
                  www.protega.ai/customer-login
                </a>
              </p>

              <div className="tw-pt-3">
                <p>
                  <strong>The Beach Company Support:</strong>
                </p>
                <p>
                  Email:{" "}
                  <a
                    className="tw-underline"
                    href="mailto:support@thebeachcompany.in"
                  >
                    support@thebeachcompany.in
                  </a>
                </p>
              </div>
            </div>
          </section>

          {/* Back link */}
          <Link
            to={"/thebeachcompany"}
            className="tw-border-t tw-border-gray-200 tw-pt-6 tw-flex tw-items-center tw-text-sm tw-text-gray-800"
          >
            <ChevronLeft /> Back to Beach Company Care
          </Link>
        </section>

        <Footer />
      </main>
    </div>
  );
};

export default ProtegaProtectionPlan;
